import React, { useCallback, useEffect, useState } from "react";
import { Checkbox, TextButton } from "@graffer-inc/console-design-kit";
import { BaseModal } from "../../../atoms/base-modal";
import classnames from "classnames";
import { matchType } from "../../../../application/domain/base/base-type";
import { CsvFileType, CsvUploader } from "../../../molecules/csv-uploader";
import { BatchImportPreview } from "../../../molecules/batch-import/batch-import-preview";
import { UserBatchImportResult } from "../../../../application/domain/user/user-batch-import-result";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../graphql/client";
import { reportUnexpectedError } from "../../../../error-reporter";
import { useAuthContextV2 } from "../../../../auth-state";

type OperationState = "SELECT" | "PREVIEW" | "RESULT";

export const BatchImportCreateUserModal: React.FC<{
  isActive: boolean;
  isBatchImporting: boolean;
  orgId: string;
  onClose: () => void;
  onImport: (
    organizationId: string,
    csv: string,
    validateOnly: boolean,
    doSendInvitation: boolean,
  ) => Promise<UserBatchImportResult | undefined>;
  onDownloadTemplateCsv: (samlAuthEnabled: boolean) => void;
  fetchUsersByOrganizationId: (orgId: string) => void;
}> = ({
  isActive,
  isBatchImporting,
  orgId,
  onClose,
  onImport,
  onDownloadTemplateCsv,
  fetchUsersByOrganizationId,
}) => {
  const [importedCount, setImportedCount] = useState(0);
  const [importingAccounts, setImportingAccounts] = useState<
    string[] | undefined
  >();
  const [pageStatus, setPageState] = useState<OperationState>("SELECT");
  const [file, setFile] = useState<CsvFileType | undefined>();
  const [result, setResult] = useState<UserBatchImportResult>();
  const { auth } = useAuthContextV2();
  const [doSendInvitation, setDoSendInvitation] = useState(false);

  const execPreview = useCallback(async () => {
    if (!file) {
      return;
    }

    const res = await onImport(orgId, file.data, true, doSendInvitation);
    if (res) {
      setResult(res);
      setPageState("PREVIEW");
    }
  }, [onImport, orgId, file, doSendInvitation]);

  const execImport = useCallback(async () => {
    if (!file) {
      return;
    }

    const res = await onImport(orgId, file.data, false, doSendInvitation);
    if (res) {
      if (res.validateResult.isValid) {
        setPageState("RESULT");
        const targetAccounts = res.validateResult.results.map(
          (r) => r.user.accountName,
        );
        setImportingAccounts(targetAccounts);
      }
      setResult(res);
    }
  }, [onImport, orgId, file, doSendInvitation]);

  const handleClose = useCallback(() => {
    fetchUsersByOrganizationId(orgId);
    onClose();
  }, [fetchUsersByOrganizationId, onClose, orgId]);

  const pollImportedCount = useCallback(
    (accountNames: string[]) => {
      const intervalId = setInterval(async () => {
        const res = await runGqlQueryV2(() =>
          gqlClient.orgUsersCountByOrgIdAndAccountNames(
            { orgId, accountNames },
            gqlHeader("q"),
          ),
        );
        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          clearInterval(intervalId);
        }
        if (res.data) {
          const count = res.data.orgUsersCountByOrgIdAndAccountNames.count;
          setImportedCount(count);
          if (count === accountNames.length) {
            clearInterval(intervalId);
            setImportingAccounts(undefined);
          }
        }
      }, 3000);
    },
    [orgId, setImportedCount, setImportingAccounts],
  );

  useEffect(() => {
    if (importingAccounts) {
      pollImportedCount(importingAccounts);
    }
  }, [importingAccounts, pollImportedCount]);

  return (
    <BaseModal
      title={matchType(pageStatus, {
        SELECT: "ユーザー一括登録",
        PREVIEW: "一括登録内容の確認",
        RESULT: "ユーザー一括登録",
      })}
      isActive={isActive}
      onClose={handleClose}
    >
      {matchType(pageStatus, {
        SELECT: (
          <>
            <section className="modal-card-body">
              <div className="mb-4">
                <p>
                  テンプレートCSVをダウンロードし、ユーザー情報を記入してアップロードしてください。
                  <TextButton
                    icon={"DownloadIcon"}
                    text="CSVをダウンロードする"
                    onClick={() =>
                      onDownloadTemplateCsv(
                        auth.data?.orgUsingFeature.useSamlAuth ?? false,
                      )
                    }
                  />
                </p>
              </div>
              <CsvUploader file={file} setFile={setFile} />
            </section>
            <footer className="modal-card-foot" style={{ display: "initial" }}>
              <div className="columns">
                <Checkbox
                  label=""
                  description={""}
                  options={[
                    {
                      value: "doSendInvitation",
                      name: "仮パスワードをメールで送信する",
                      description:
                        "CSV内のすべてのユーザーでメールアドレスの入力が必須です。",
                    },
                  ]}
                  values={doSendInvitation ? ["doSendInvitation"] : []}
                  onChange={(e) => {
                    setDoSendInvitation(e.target.checked);
                  }}
                />
              </div>
              <div className="buttons" style={{ flexWrap: "initial" }}>
                <button
                  className="button is-failure is-fullwidth"
                  onClick={handleClose}
                >
                  キャンセル
                </button>
                <button
                  className={classnames("button is-info is-fullwidth")}
                  onClick={execPreview}
                  disabled={!file || isBatchImporting}
                >
                  内容の確認に進む
                </button>
              </div>
            </footer>
          </>
        ),
        PREVIEW: result && (
          <BatchImportPreview
            process="一括登録"
            file={file}
            validateResult={result.validateResult}
            execImport={execImport}
            isBatchImporting={isBatchImporting}
            returnButtonEvent={() => setPageState("SELECT")}
            doSendInvitation={doSendInvitation}
            orgId={orgId}
          />
        ),
        RESULT: (
          <>
            {importingAccounts ? (
              <section className="modal-card-body">
                一括登録の実行中です。
                <progress
                  className="progress is-info"
                  value={importedCount}
                  max={importingAccounts.length}
                />
                <p className="help">
                  件数次第で処理完了まで数分かかる場合があります。モーダルを閉じても一括登録処理は続行されます。
                </p>
              </section>
            ) : (
              <section className="modal-card-body">
                {importedCount}件の一括登録が完了しました
              </section>
            )}
            <footer className="modal-card-foot">
              <button
                className={classnames("button is-fullwidth")}
                onClick={handleClose}
                disabled={false}
              >
                閉じる
              </button>
            </footer>
          </>
        ),
      })}
    </BaseModal>
  );
};
